
/* Selling plan names that get a hidden `_plan_name` line-item property on their refill(s).
   Kept as an explicit allowlist so one-time purchases (and any future plans) don't pick it up
   unless deliberately added here. Stored lowercase; matching is case-insensitive + trimmed,
   so "Discovery Plan", "Discovery plan", " discovery plan " all match. */
const PLAN_NAME_PROPERTY_ALLOWLIST = ["starter plan", "discovery plan"];

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.refillsCollectionId = this.dataset.refillsCollectionId;
    // Optional dedicated Recharge bundle product for the Discovery Plan. When unset, the
    // bundle parent falls back to this page's bundle product + the selected plan variant.
    this.bundleProductId = this.dataset.bundleProductId || null;
    this.bundleVariantId = this.dataset.bundleVariantId || null;
    this.bundleSellingPlanId = this.dataset.bundleSellingPlanId || null;
    this.state = {
      variant: null,
      shell: null,
      refills: [],
      currentStep: 1,
      furthestStep: 1,
    };
  }

  connectedCallback() {
    this.cacheElements();
    this.readLabels();
    this.readIngredients();
    this.readScentPlanData();
    this.bindPlanEvents();
    this.bindShellEvents();
    this.bindScentEvents();
    this.bindNavigationEvents();
    this.bindModalEvents();
    this.bindCtaEvents();
    this.syncPlanFromDom();
    this.updateScentTitle();
    this.renderCta();
    this.renderStep();
  }

  /* ----- DOM cache ----- */
  cacheElements() {
    this.heroImage = this.querySelector(".bundle-builder__hero");
    this.stepSections = this.querySelectorAll(".bundle-step");

    this.planInputs = this.querySelectorAll(".bundle-plan__input");
    this.shellInputs = this.querySelectorAll(".bundle-shell__input");
    this.scentInputs = this.querySelectorAll(".bundle-scent__input");
    this.scentItems = this.querySelectorAll(".bundle-scent");

    this.nextBtns = this.querySelectorAll("[data-step-next]");
    this.backBtns = this.querySelectorAll("[data-step-back]");
    this.shellNextBtn = this.querySelector('[data-step-next="3"]');
    this.ctaBtn = this.querySelector("[data-add-to-cart]");
    this.ctaPrice = this.querySelector("[data-cta-price]");
    this.ctaText = this.querySelector(".bundle-cta--get-set .bundle-cta__text");

    this.scentTitle = this.querySelector("[data-scent-pick-label]");

    // All three accordion bodies (ingredients, howtouse, howrefills).
    this.accordionBodies = {};
    this.accordionFallbacks = {};
    ["ingredients", "howtouse", "howrefills"].forEach((key) => {
      const el = this.querySelector(`[data-accordion-body="${key}"]`);
      if (el) {
        this.accordionBodies[key] = el;
        this.accordionFallbacks[key] = el.innerHTML;
      }
    });

    this.subscriptionModal = this.querySelector("[data-subscription-modal]");
    this.modalOpenBtns = this.querySelectorAll("[data-open-subscription-modal]");
    this.modalCloseBtn = this.querySelector("[data-close-subscription-modal]");

    this.qtyIncBtns = this.querySelectorAll("[data-qty-increment]");
    this.qtyDecBtns = this.querySelectorAll("[data-qty-decrement]");
  }

  readLabels() {
    const el = this.querySelector("[data-bundle-labels]");
    this.labels = el ? JSON.parse(el.textContent) : { pick_1: "Pick 1 scent", pick_2: "Pick 2 scents" };
  }

  readIngredients() {
    const el = this.querySelector("[data-accordion-data]");
    try {
      this.accordionData = el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      console.warn("bundle-builder: failed to parse accordion data", e);
      this.accordionData = {};
    }
  }

  readScentPlanData() {
    this.scentPlanData = [...this.querySelectorAll(".bundle-scent-plan-data")].map((el) => ({
      productId: el.closest(".bundle-scent").dataset.scentProductId,
      planName: el.dataset.planName,
      planId: el.dataset.planId,
    }));
  }

  /* ----- Events ----- */

  bindPlanEvents() {
    this.planInputs.forEach((input) =>
      input.addEventListener("change", () => this.onPlanChange(input))
    );
  }

  bindShellEvents() {
    this.shellInputs.forEach((input) =>
      input.addEventListener("change", () => this.onShellChange(input))
    );
  }

  bindScentEvents() {
    this.scentInputs.forEach((input) =>
      input.addEventListener("change", () => this.onScentToggle(input))
    );
    this.qtyIncBtns.forEach((btn) =>
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onQtyChange(btn.closest(".bundle-scent"), +1);
      })
    );
    this.qtyDecBtns.forEach((btn) =>
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onQtyChange(btn.closest(".bundle-scent"), -1);
      })
    );
  }

  bindNavigationEvents() {
    this.nextBtns.forEach((btn) =>
      btn.addEventListener("click", () => {
        const target = parseInt(btn.dataset.stepNext, 10);
        this.goToStep(target);
      })
    );
    this.backBtns.forEach((btn) =>
      btn.addEventListener("click", () => {
        const target = parseInt(btn.dataset.stepBack, 10);
        this.goToStep(target);
      })
    );
  }

  bindModalEvents() {
    this.modalOpenBtns.forEach((btn) =>
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.subscriptionModal?.showModal) this.subscriptionModal.showModal();
      })
    );
    if (this.modalCloseBtn) {
      this.modalCloseBtn.addEventListener("click", () => this.subscriptionModal?.close?.());
    }
    if (this.subscriptionModal) {
      this.subscriptionModal.addEventListener("click", (e) => {
        if (e.target === this.subscriptionModal) this.subscriptionModal.close();
      });
    }
  }

  bindCtaEvents() {
    if (this.ctaBtn) {
      this.ctaBtn.addEventListener("click", () => this.submitBundle());
    }
  }

  /* ----- State updates ----- */

  syncPlanFromDom() {
    const checked = [...this.planInputs].find((i) => i.checked);
    if (checked) this.onPlanChange(checked);
  }

  onPlanChange(input) {
    const card = input.closest(".bundle-plan");
    const prevRefillCount = this.state.variant?.refillCount;
    const sellingPlanName = card.dataset.sellingPlanName?.trim() || null;
    let defaultScentIds = [];
    try {
      defaultScentIds = JSON.parse(card.dataset.defaultScentIds || "[]");
    } catch (e) {
      defaultScentIds = [];
    }
    this.state.variant = {
      id: card.dataset.variantId,
      refillCount: parseInt(card.dataset.refillCount, 10),
      sellingPlanName: sellingPlanName,
      sellingPlanId: card.dataset.sellingPlanId || null,
      displayPrice: parseInt(card.dataset.displayPrice, 10),
      comparePrice: parseInt(card.dataset.comparePrice, 10) || 0,
      savePercent: parseInt(card.dataset.savePercent, 10) || 0,
      defaultScentIds: defaultScentIds,
      card: card,
    };
    const refillCountChanged = prevRefillCount !== undefined && prevRefillCount !== this.state.variant.refillCount;
    const isInitialLoad = prevRefillCount === undefined;
    if (refillCountChanged) {
      this.resetScentsOnPlanChange();
    }
    if (refillCountChanged || isInitialLoad) {
      this.applyDefaultScents();
    }
    this.updateScentTitle();
    this.syncSubscriptionModalBody();
    this.renderCta();
  }

  applyDefaultScents() {
    const ids = this.state.variant?.defaultScentIds || [];
    if (!ids.length) return;
    const max = this.state.variant.refillCount || ids.length;
    ids.slice(0, max).forEach((id) => {
      const item = [...this.scentItems].find((el) => String(el.dataset.scentProductId) === String(id));
      if (!item) return;
      const input = item.querySelector(".bundle-scent__input");
      if (input) input.checked = true;
      this.state.refills.push({
        productId: item.dataset.scentProductId,
        variantId: item.dataset.scentVariantId,
        handle: item.dataset.scentHandle,
        image: item.dataset.scentImage,
        title: item.querySelector(".bundle-scent__name")?.textContent.trim() || item.dataset.scentHandle,
        quantity: 1,
      });
    });
    this.renderScentUI();
    this.renderIngredients();
    this.renderCta();
  }

  syncSubscriptionModalBody() {
    const bodyEl = this.querySelector("[data-subscription-modal-body]");
    if (!bodyEl || !this.state.variant?.card) return;
    const tpl = this.state.variant.card.querySelector("[data-plan-modal-body]");
    if (tpl && tpl.innerHTML.trim()) {
      bodyEl.innerHTML = tpl.innerHTML;
    }
  }

  onShellChange(input) {
    const card = input.closest(".bundle-shell");
    this.state.shell = {
      productId: card.dataset.shellProductId,
      variantId: card.dataset.shellVariantId,
      handle: card.dataset.shellHandle,
      image: card.dataset.shellImage,
    };
    if (this.shellNextBtn) this.shellNextBtn.disabled = false;
    if (this.heroImage && this.state.shell.image) {
      this.heroImage.src = this.state.shell.image;
    }
    this.renderCta();
  }

  onScentToggle(input) {
    const item = input.closest(".bundle-scent");
    const max = this.state.variant ? this.state.variant.refillCount : 1;

    if (input.checked) {
      const currentTotalQty = this.state.refills.reduce((sum, r) => sum + r.quantity, 0);
      if (currentTotalQty >= max) {
        // If only 1 slot available and user clicks a new one, replace the existing
        if (max === 1 && this.state.refills.length === 1) {
          const prev = this.state.refills[0];
          this.state.refills = [];
          const prevInput = this.querySelector(`.bundle-scent__input[value="${prev.variantId}"]`);
          if (prevInput) prevInput.checked = false;
        } else {
          input.checked = false;
          return;
        }
      }
      this.state.refills.push({
        productId: item.dataset.scentProductId,
        variantId: item.dataset.scentVariantId,
        handle: item.dataset.scentHandle,
        image: item.dataset.scentImage,
        title: item.querySelector(".bundle-scent__name")?.textContent.trim() || item.dataset.scentHandle,
        quantity: 1,
      });
      // Hero image follows the most recently selected refill.
      if (this.heroImage && item.dataset.scentImage) {
        this.heroImage.src = item.dataset.scentImage;
      }
    } else {
      this.state.refills = this.state.refills.filter(
        (r) => r.variantId !== item.dataset.scentVariantId
      );
      // If any refill remains, show the last one's image; else fall back to the shell or initial hero.
      if (this.heroImage) {
        const last = this.state.refills[this.state.refills.length - 1];
        if (last?.image) {
          this.heroImage.src = last.image;
        } else if (this.state.shell?.image) {
          this.heroImage.src = this.state.shell.image;
        } else {
          this.heroImage.src = this.heroImage.dataset.initialSrc || this.heroImage.src;
        }
      }
    }
    this.renderScentUI();
    this.renderIngredients();
    this.renderCta();
  }

  onQtyChange(item, delta) {
    if (!this.state.variant || this.state.variant.refillCount !== 2) return;
    const variantId = item.dataset.scentVariantId;
    const refill = this.state.refills.find((r) => r.variantId === variantId);
    if (!refill) return;
    const max = this.state.variant.refillCount;
    const currentTotalOther = this.state.refills
      .filter((r) => r.variantId !== variantId)
      .reduce((sum, r) => sum + r.quantity, 0);
    const newQty = refill.quantity + delta;
    if (newQty < 1) {
      // Uncheck scent
      this.state.refills = this.state.refills.filter((r) => r.variantId !== variantId);
      const input = item.querySelector(".bundle-scent__input");
      if (input) input.checked = false;
      this.renderScentUI();
      this.renderIngredients();
      this.renderCta();
      return;
    }
    if (currentTotalOther + newQty > max) return;
    refill.quantity = newQty;
    this.renderScentUI();
    this.renderCta();
  }

  resetScentsOnPlanChange() {
    // Wipe all refills and force re-selection (simplest, safest UX).
    this.state.refills = [];
    this.scentInputs.forEach((i) => { i.checked = false; });
    this.renderScentUI();
    this.renderIngredients();
  }

  goToStep(target) {
    // Can only jump forward one step at a time, but back freely.
    if (target > this.state.furthestStep + 1) return;
    // Guards: enforce selections for forward moves
    if (target === 2 && !this.state.variant) return;
    if (target === 3 && !this.state.shell) return;

    this.state.currentStep = target;
    if (target > this.state.furthestStep) this.state.furthestStep = target;
    if (target === 3 && this.heroImage && this.state.refills.length > 0) {
      const last = this.state.refills[this.state.refills.length - 1];
      if (last?.image) this.heroImage.src = last.image;
    }
    this.renderStep();
    this.scrollToForm();
  }

  scrollToForm() {
    if (window.innerWidth < 900) {
      const form = this.querySelector(".bundle-builder__form");
      if (form) form.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  /* ----- Renderers ----- */

  renderStep() {
    this.stepSections.forEach((section) => {
      const step = parseInt(section.dataset.step, 10);
      section.hidden = step !== this.state.currentStep;
    });
  }

  updateScentTitle() {
    if (!this.scentTitle || !this.state.variant) return;
    const n = this.state.variant.refillCount;
    this.scentTitle.textContent = n === 2 ? this.labels.pick_2 : this.labels.pick_1;
  }

  renderScentUI() {
    const showQty = this.state.variant && this.state.variant.refillCount === 2;
    this.scentItems.forEach((item) => {
      const variantId = item.dataset.scentVariantId;
      const refill = this.state.refills.find((r) => r.variantId === variantId);
      const qtyEl = item.querySelector("[data-qty-controls]");
      const qtyValueEl = item.querySelector("[data-qty-value]");
      if (!qtyEl) return;
      if (refill && showQty) {
        qtyEl.hidden = false;
        if (qtyValueEl) qtyValueEl.textContent = refill.quantity;
      } else {
        qtyEl.hidden = true;
      }
    });
  }

  renderIngredients() {

    ["ingredients", "howtouse", "howrefills"].forEach((key) => {
      const body = this.accordionBodies[key];
      if (!body) return;

      if (this.state.refills.length === 0) {
        body.innerHTML = this.accordionFallbacks[key] || "";
        return;
      }

      if (key !== "ingredients") {
        const first = this.state.refills[0];
        const data = first && this.accordionData && this.accordionData[first.handle];
        const content = data && data[key];
        body.innerHTML = content || this.accordionFallbacks[key] || "";
        return;
      }

      const seen = new Set();
      const blocks = [];
      this.state.refills.forEach((r) => {
        if (seen.has(r.handle)) return;
        seen.add(r.handle);
        const data = this.accordionData && this.accordionData[r.handle];
        const content = data && data[key];
        if (!content) return;
        if (this.state.refills.length > 1) {
          const niceName = r.handle
            .replace(/^free-/, "")
            .replace(/-/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
          blocks.push(`<h4 class="bundle-ingredients__name">${niceName}</h4><div class="bundle-ingredients__body">${content}</div>`);
        } else {
          blocks.push(content);
        }
      });

      body.innerHTML = blocks.length ? blocks.join("") : (this.accordionFallbacks[key] || "");
    });
  }

  renderCta() {
    if (!this.ctaBtn || !this.state.variant) return;
    const totalQty = this.state.refills.reduce((sum, r) => sum + r.quantity, 0);
    const required = this.state.variant.refillCount;
    this.ctaBtn.disabled = totalQty !== required || !this.state.shell;
    if (this.ctaPrice) {
      this.ctaPrice.textContent = this.formatMoney(this.state.variant.displayPrice);
    }
  }

  formatMoney(pence) {
    if (typeof pence !== "number" || isNaN(pence)) return "";
    const pounds = pence / 100;
    const str = Number.isInteger(pounds) ? pounds.toFixed(0) : pounds.toFixed(2);
    return "£" + str;
  }

  /* ----- Cart submission ----- */

  /* Returns { _plan_name } when the selected plan is in the allowlist (Starter / Discovery),
     otherwise an empty object so it spreads to nothing for one-time purchases. */
  planNameProperty() {
    const name = this.state.variant?.sellingPlanName?.trim();
    if (name && PLAN_NAME_PROPERTY_ALLOWLIST.includes(name.toLowerCase())) {
      return { _plan_name: name };
    }
    return {};
  }

  resolveSellingPlanIdFor(refill) {
    const name = this.state.variant?.sellingPlanName;
    if (!name) return null;
    const needle = name.toLowerCase().trim();
    const match = this.scentPlanData.find((p) => {
      const planName = p.planName?.toLowerCase().trim();
      if (!planName || p.productId !== refill.productId) return false;
      return planName.includes(needle) || needle.includes(planName);
    });
    return match ? match.planId : null;
  }

  /* True only for the Discovery Plan: a subscription with 2 refills. The Starter
     subscription (1 refill) and the one-time purchase keep the individual-line-item flow. */
  isDiscoveryBundle() {
    return (
      !!this.state.variant?.sellingPlanName &&
      this.state.variant?.refillCount === 2
    );
  }

  /* Human-readable list of the chosen refills for display in the cart drawer.
     Stored on a hidden (_-prefixed) property so it shows in the drawer but not at checkout.
     Items separated by "~~", e.g. "2x Sage & Sea Salt~~1x Bergamot & Cedarwood". */
  buildBundleContents() {
    return this.state.refills
      .map((r) => `${r.quantity}x ${r.title}`)
      .join("~~");
  }

  /* Recharge fixed-price bundle selections — refills only (the shell is not a bundle item). */
  buildBundleSelections() {
    return this.state.refills.map((r) => ({
      collectionId: this.refillsCollectionId,
      externalProductId: r.productId,
      externalVariantId: r.variantId,
      quantity: r.quantity,
    }));
  }

  /* Ask Recharge for the bundle id (_rb_id) that encodes the selected refills.
     Parent = this bundle product + the selected plan variant. */
  async getBundleId() {
    const bundle = {
      externalProductId: this.bundleProductId || this.productId,
      externalVariantId: this.bundleVariantId || this.state.variant.id,
      selections: this.buildBundleSelections(),
    };
    try {
      return await recharge.bundle.getBundleId(bundle);
    } catch (error) {
      console.error("bundle-builder: failed to get Recharge bundle id", error);
      return null;
    }
  }

  /* Current behaviour: each refill + the shell as their own line items, linked by _unique_id. */
  buildIndividualItems(uniqueId) {
    const items = [];

    this.state.refills.forEach((refill, idx) => {
      const props = { _unique_id: uniqueId, ...this.planNameProperty() };
      props[`_prodcut-handle${idx}`] = refill.handle;
      const sellingPlanId = this.resolveSellingPlanIdFor(refill);
      const item = {
        id: refill.variantId,
        quantity: refill.quantity,
        properties: props,
      };
      if (sellingPlanId) {
        item.selling_plan = sellingPlanId;
      }
      items.push(item);
    });

    items.push(this.buildShellItem(uniqueId));
    return items;
  }

  buildShellItem(uniqueId) {
    return {
      id: this.state.shell.variantId,
      quantity: 1,
      properties: {
        _unique_id: uniqueId,
        "_prodcut-handle_shell": this.state.shell.handle,
      },
    };
  }

  async buildCartPayload() {
    const uniqueId = Date.now();

    if (this.isDiscoveryBundle()) {
      const rbId = await this.getBundleId();
      if (!rbId) {
        // Surface the failure rather than silently adding a malformed bundle.
        throw new Error("Could not build the subscription bundle. Please try again.");
      }
      const bundleItem = {
        id: this.bundleVariantId || this.state.variant.id,
        quantity: 1,
        properties: {
          _rb_id: rbId,
          _unique_id: uniqueId,
          _bundle_contents: this.buildBundleContents(),
          ...this.planNameProperty(),
        },
      };
      const sellingPlanId = this.bundleSellingPlanId || this.state.variant.sellingPlanId;
      if (sellingPlanId) {
        bundleItem.selling_plan = sellingPlanId;
      }
      return { items: [bundleItem, this.buildShellItem(uniqueId)] };
    }

    return { items: this.buildIndividualItems(uniqueId) };
  }

  async submitBundle() {
    if (!this.state.variant || !this.state.shell || this.state.refills.length === 0) return;
    const originalHTML = this.ctaBtn.innerHTML;
    this.ctaBtn.disabled = true;
    this.ctaBtn.textContent = "Adding…";

    try {
      const payload = await this.buildCartPayload();
      // TEMP DIAGNOSTIC — remove after debugging. Shows exactly what we send to /cart/add,
      // incl. whether _plan_name made it onto the (discovery) bundle line item.
      console.log("[bundle-builder] sellingPlanName:", this.state.variant?.sellingPlanName, "| isDiscoveryBundle:", this.isDiscoveryBundle(), "| payload items:", JSON.stringify(payload.items, null, 2));
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        payload.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
        payload.sections_url = window.location.pathname;
      }

      const res = await fetch(`${window.Shopify?.routes?.root ?? "/"}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/javascript",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || `Add to cart failed: ${res.status}`);

      // Refresh the cart drawer's rendered sections with the updated cart.
      if (cartDrawer && data.sections) {
        if (typeof cartDrawer.renderContents === "function") {
          cartDrawer.renderContents(data);
        } else {
          // Fallback: replace section innerHTML directly.
          Object.keys(data.sections).forEach((sectionId) => {
            const target = document.getElementById(`shopify-section-${sectionId}`) || document.querySelector(`[data-section-id="${sectionId}"]`);
            if (target) target.innerHTML = data.sections[sectionId];
          });
        }
        // Open the drawer (covers the common Dawn-like and custom APIs).
        if (typeof cartDrawer.open === "function") cartDrawer.open();
        else cartDrawer.classList.add("active", "is-open");
      }

      // Notify anything else listening (analytics, cart count bubble, etc.).
      document.dispatchEvent(new CustomEvent("cart:refresh", { detail: data }));

      // Restore the CTA.
      this.ctaBtn.innerHTML = originalHTML;
      this.ctaBtn.disabled = false;
    } catch (err) {
      console.error("bundle-builder:", err);
      // Briefly surface the failure on the CTA, then restore it so the user can retry.
      this.ctaBtn.textContent = err.message || "Something went wrong. Please try again.";
      setTimeout(() => {
        this.ctaBtn.innerHTML = originalHTML;
        this.ctaBtn.disabled = false;
      }, 2500);
    }
  }
}

if (!customElements.get("bundle-builder")) {
  customElements.define("bundle-builder", BundleBuilder);
}
